<template>
  <div class="toolbar-root">
    <div ref="scrollEl" class="toolbar-scroll">
      <div class="toolbar-track">
        <!-- Undo / Redo -->
        <div class="flex items-center gap-1 pr-2">
          <el-tooltip content="Назад" :show-after="300" placement="top">
            <button
              class="toolbar-btn"
              :disabled="!editor.can().undo()"
              @click="editor.chain().focus().undo().run()"
            >
              <svg-icon
                name="huge-undo"
                class="w-4 h-4 fill-none text-foreground"
              />
            </button>
          </el-tooltip>
          <el-tooltip content="Вперед" :show-after="300" placement="top">
            <button
              class="toolbar-btn"
              :disabled="!editor.can().redo()"
              @click="editor.chain().focus().redo().run()"
            >
              <svg-icon
                name="huge-redo"
                class="w-4 h-4 fill-none text-foreground"
              />
            </button>
          </el-tooltip>
        </div>

        <div class="toolbar-divider"></div>

        <!-- Font family + size -->
        <div class="flex items-center gap-1 px-2">
          <DropdownMenu>
            <el-tooltip content="Шрифт" :show-after="300" placement="top">
              <DropdownMenuTrigger as-child>
                <button class="toolbar-select-btn">
                  {{ currentFontFamily || "Шрифт" }}
                  <el-icon class="ml-0.5"><arrow-down /></el-icon>
                </button>
              </DropdownMenuTrigger>
            </el-tooltip>
            <DropdownMenuContent>
              <DropdownMenuItem
                v-for="font in fontFamilies"
                :key="font"
                :style="{ fontFamily: font }"
                :class="currentFontFamily === font ? 'text-foreground' : ''"
                @click="editor.chain().focus().setFontFamily(font).run()"
              >
                {{ font }}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <el-tooltip
              content="Размер шрифта"
              :show-after="300"
              placement="top"
            >
              <DropdownMenuTrigger as-child>
                <button class="toolbar-select-btn toolbar-select-btn--sm">
                  {{ currentFontSize || "Pt" }}
                  <el-icon class="ml-0.5"><arrow-down /></el-icon>
                </button>
              </DropdownMenuTrigger>
            </el-tooltip>
            <DropdownMenuContent>
              <DropdownMenuItem
                v-for="size in fontSizes"
                :key="size"
                :class="
                  currentFontSize === String(size) ? 'text-foreground' : ''
                "
                @click="
                  editor
                    .chain()
                    .focus()
                    .setFontSize(size + 'pt')
                    .run()
                "
              >
                {{ size }}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div class="toolbar-divider"></div>

        <!-- Formatting -->
        <div class="flex items-center gap-1 px-2">
          <el-tooltip content="Жирный" :show-after="300" placement="top">
            <button
              class="toolbar-btn"
              :class="{ active: editor.isActive('bold') }"
              @click="editor.chain().focus().toggleBold().run()"
            >
              <svg-icon
                name="huge-editor-bold"
                class="w-4 h-4 fill-none text-foreground"
              />
            </button>
          </el-tooltip>

          <el-tooltip content="Курсив" :show-after="300" placement="top">
            <button
              class="toolbar-btn"
              :class="{ active: editor.isActive('italic') }"
              @click="editor.chain().focus().toggleItalic().run()"
            >
              <svg-icon
                name="huge-editor-italic"
                class="w-4 h-4 fill-none text-foreground"
              />
            </button>
          </el-tooltip>

          <el-tooltip content="Подчёркнутый" :show-after="300" placement="top">
            <button
              class="toolbar-btn"
              :class="{ active: editor.isActive('underline') }"
              @click="editor.chain().focus().toggleUnderline().run()"
            >
              <svg-icon
                name="huge-editor-underline"
                class="w-4 h-4 fill-none text-foreground"
              />
            </button>
          </el-tooltip>

          <el-tooltip content="Зачёркнутый" :show-after="300" placement="top">
            <button
              class="toolbar-btn"
              :class="{ active: editor.isActive('strike') }"
              @click="editor.chain().focus().toggleStrike().run()"
            >
              <svg-icon
                name="huge-editor-strikethrough"
                class="w-4 h-4 fill-none text-foreground"
              />
            </button>
          </el-tooltip>
        </div>

        <div class="toolbar-divider"></div>

        <!-- Alignment -->
        <div class="flex items-center gap-1 px-2">
          <DropdownMenu>
            <el-tooltip
              content="Выравнивание"
              :show-after="300"
              placement="top"
            >
              <DropdownMenuTrigger as-child>
                <button class="toolbar-btn">
                  <svg-icon
                    name="huge-editor-align-left"
                    class="w-4 h-4 fill-none text-foreground"
                  />
                </button>
              </DropdownMenuTrigger>
            </el-tooltip>
            <DropdownMenuContent>
              <DropdownMenuItem
                v-for="align in alignmentOptions"
                :key="align.value"
                @click="editor.chain().focus().setTextAlign(align.value).run()"
              >
                <div class="flex items-center gap-2 text-sm">
                  <svg-icon :name="align.iconName" class="w-4 h-4 fill-none" />
                  <span>{{ align.label }}</span>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div class="toolbar-divider"></div>

        <!-- Table + Link -->
        <div class="flex items-center gap-1 px-2">
          <!-- Table grid picker -->
          <el-tooltip content="Таблица" :show-after="300" placement="top">
            <TableGridPicker :editor="editor" />
          </el-tooltip>

          <!-- Link -->
          <el-tooltip content="Ссылка" :show-after="300" placement="top">
            <LinkPopover :editor="editor">
              <button
                class="toolbar-btn"
                :class="{ active: editor.isActive('link') }"
              >
                <svg-icon
                  name="huge-editor-link"
                  class="w-4 h-4 fill-none text-foreground"
                />
              </button>
            </LinkPopover>
          </el-tooltip>
        </div>

        <div class="toolbar-divider"></div>

        <!-- Spacing -->
        <div class="flex items-center gap-1 px-2">
          <DropdownMenu>
            <el-tooltip
              content="Интервал между абзацами"
              :show-after="300"
              placement="top"
            >
              <DropdownMenuTrigger as-child>
                <button class="toolbar-select-btn toolbar-select-btn--sm">
                  {{ currentParagraphSpacing }}pt
                  <el-icon class="ml-0.5"><arrow-down /></el-icon>
                </button>
              </DropdownMenuTrigger>
            </el-tooltip>
            <DropdownMenuContent>
              <DropdownMenuItem
                v-for="val in paragraphSpacingOptions"
                :key="val"
                :class="
                  currentParagraphSpacing === String(val)
                    ? 'text-foreground'
                    : ''
                "
                @click="
                  editor.chain().focus().setParagraphSpacingAfter(val).run()
                "
              >
                {{ val }}pt
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <el-tooltip
              content="Межстрочный интервал"
              :show-after="300"
              placement="top"
            >
              <DropdownMenuTrigger as-child>
                <button class="toolbar-select-btn toolbar-select-btn--sm">
                  {{ currentLineSpacing || "Интервал" }}
                  <el-icon class="ml-0.5"><arrow-down /></el-icon>
                </button>
              </DropdownMenuTrigger>
            </el-tooltip>
            <DropdownMenuContent>
              <DropdownMenuItem
                v-for="val in lineSpacingValues"
                :key="val"
                :class="
                  currentLineSpacing === String(val) ? 'text-foreground' : ''
                "
                @click="editor.chain().focus().setLineSpacing(val).run()"
              >
                {{ val }}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>

    <div
      class="toolbar-fade toolbar-fade--left"
      :class="{ 'is-visible': canScrollLeft }"
    />
    <div
      class="toolbar-fade toolbar-fade--right"
      :class="{ 'is-visible': canScrollRight }"
    />

    <button
      v-show="canScrollLeft"
      type="button"
      class="toolbar-scroll-btn toolbar-scroll-btn--left"
      tabindex="-1"
      @click="scrollByDir(-1)"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polyline points="15 18 9 12 15 6" />
      </svg>
    </button>
    <button
      v-show="canScrollRight"
      type="button"
      class="toolbar-scroll-btn toolbar-scroll-btn--right"
      tabindex="-1"
      @click="scrollByDir(1)"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polyline points="9 18 15 12 9 6" />
      </svg>
    </button>
  </div>
</template>

<script>
import { ref } from "vue";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import TableGridPicker from "./toolbar/TableGridPicker.vue";
import LinkPopover from "./toolbar/LinkPopover.vue";
import { useScrollFade } from "../composables/useScrollFade";

export default {
  name: "TipTapToolbar",
  components: {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    TableGridPicker,
    LinkPopover,
  },

  setup() {
    const scrollEl = ref(null);
    const { canScrollLeft, canScrollRight, scrollByDir } =
      useScrollFade(scrollEl);
    return { scrollEl, canScrollLeft, canScrollRight, scrollByDir };
  },

  props: {
    editor: {
      type: Object,
      required: true,
    },
    defaultFontFamily: {
      type: String,
      default: "Arial",
    },
    defaultFontSize: {
      type: String,
      default: "11pt",
    },
    defaultLineSpacing: {
      type: Number,
      default: 1.15,
    },
  },

  data() {
    return {
      paragraphSpacingOptions: [0, 3, 6, 8, 10, 12, 18, 24],
    };
  },

  computed: {
    fontFamilies() {
      return [
        "Times New Roman",
        "Arial",
        "Courier New",
        "Georgia",
        "Verdana",
        "Calibri",
      ];
    },
    fontSizes() {
      return [8, 9, 10, 11, 12, 14, 16, 18, 20, 24, 28, 32, 36, 48, 60, 72];
    },
    lineSpacingValues() {
      return [1, 1.15, 1.5, 2, 2.5, 3];
    },
    currentFontFamily() {
      if (!this.editor) return this.defaultFontFamily;
      return (
        this.editor.getAttributes("textStyle").fontFamily ||
        this.defaultFontFamily
      );
    },
    currentFontSize() {
      if (!this.editor) return this.defaultFontSize.replace("pt", "");
      const size = this.editor.getAttributes("textStyle").fontSize;
      return size
        ? size.replace("pt", "")
        : this.defaultFontSize.replace("pt", "");
    },
    currentLineSpacing() {
      if (!this.editor) return String(this.defaultLineSpacing);
      const { lineHeight } = this.editor.getAttributes("paragraph");
      return lineHeight ? String(lineHeight) : String(this.defaultLineSpacing);
    },
    currentParagraphSpacing() {
      if (!this.editor) return "0";
      const { spacingAfter } = this.editor.getAttributes("paragraph");
      return spacingAfter != null ? String(spacingAfter) : "0";
    },
    alignmentOptions() {
      return [
        {
          label: "По левому краю",
          iconName: "huge-editor-align-left",
          value: "left",
        },
        {
          label: "По центру",
          iconName: "huge-editor-align-center",
          value: "center",
        },
        {
          label: "По правому краю",
          iconName: "huge-editor-align-right",
          value: "right",
        },
        {
          label: "По ширине",
          iconName: "tt-align-justify",
          value: "justify",
        },
      ];
    },
  },
};
</script>

<style lang="scss">
.toolbar-root {
  @apply relative w-full min-w-0 h-full flex items-center;
}

.toolbar-scroll {
  @apply w-full h-full overflow-x-auto;

  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
}

.toolbar-track {
  @apply inline-flex items-center h-full bg-transparent;

  ul {
    li {
      @apply text-foreground;
    }
  }
}

.toolbar-fade {
  @apply absolute top-0 bottom-0 w-10 pointer-events-none z-10;
  opacity: 0;
  transition: opacity 150ms ease;

  &.is-visible {
    opacity: 1;
  }

  &--left {
    left: 0;
    background: linear-gradient(
      to right,
      hsl(var(--background)),
      hsl(var(--background) / 0)
    );
  }

  &--right {
    right: 0;
    background: linear-gradient(
      to left,
      hsl(var(--background)),
      hsl(var(--background) / 0)
    );
  }
}

.toolbar-scroll-btn {
  @apply absolute top-1/2 -translate-y-1/2 z-20;
  @apply flex items-center justify-center;
  @apply w-6 h-6 rounded-full text-muted-foreground;
  @apply bg-background border border-border shadow-sm;
  @apply transition-colors duration-150;

  svg {
    @apply w-3.5 h-3.5;
  }

  &:hover {
    @apply text-foreground bg-muted;
  }

  &--left {
    left: 2px;
  }

  &--right {
    right: 2px;
  }
}

.toolbar-btn {
  @apply p-2 rounded-md flex items-center justify-center;
  @apply transition-all duration-150;
  @apply active:scale-95;
  @apply disabled:opacity-40;
  min-width: 32px;
  min-height: 32px;

  &:hover {
    background-color: hsl(var(--tt-button-hover-bg-color));
  }

  &.active {
    background-color: hsl(var(--tt-button-active-bg-color));
    @apply text-foreground;
  }
}

.toolbar-divider {
  width: 1px;
  height: 16px;
  @apply bg-border flex-shrink-0;
  margin: 0 4px;
}

.toolbar-select-btn {
  @apply text-xs text-foreground bg-transparent cursor-pointer;
  @apply py-1 px-1.5 rounded flex items-center gap-0.5 whitespace-nowrap;
  @apply transition-colors duration-150;
  font-size: 12px;
  height: 28px;
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;

  &:hover {
    @apply bg-muted;
  }

  &--sm {
    max-width: 70px;
  }
}

@media (max-width: 800px) {
  .toolbar-track ul {
    @apply gap-2 px-1;
  }

  .toolbar-btn {
    @apply p-1;
    min-width: 24px;
    min-height: 24px;
  }
}
</style>
